// shell/components/entity-fields/view-renderers/tag-list-renderer.ts

import { createElement } from '../../../utils/dom';
import type { TagListViewField } from '../../entity-view/types';

/**
 * Renders a list of tags as badges.
 */
export function renderTagListView<T>(
    field: TagListViewField<T>,
    value: unknown,
    model: T
): HTMLElement {
    const container = createElement('div', {
        className: 'ev-field-view ev-field-view--tag-list',
    });

    if (value === null || value === undefined) {
        container.classList.add('ev-field-view--empty');
        container.textContent = '—';
        return container;
    }

    // Handle array of strings or objects
    let tags: string[] = [];

    if (Array.isArray(value)) {
        tags = value.map(item => {
            if (typeof item === 'string') {
                return item;
            }
            if (typeof item === 'object' && item !== null && field.displayKey) {
                return String((item as Record<string, unknown>)[field.displayKey as string] ?? '');
            }
            return String(item);
        }).filter(tag => tag !== '');
    } else if (typeof value === 'string') {
        // Handle comma-separated string
        tags = value.split(',').map(s => s.trim()).filter(s => s !== '');
    }

    if (tags.length === 0) {
        container.classList.add('ev-field-view--empty');
        container.textContent = '—';
        return container;
    }

    const tagList = createElement('div', {
        className: 'ev-tag-list',
    });

    for (const tag of tags) {
        const tagEl = createElement('span', {
            className: 'ev-tag',
            textContent: tag,
        });

        // Apply color if colorFn provided
        if (field.colorFn) {
            const color = field.colorFn(tag, model);
            if (color) {
                tagEl.style.backgroundColor = color;
                tagEl.style.color = getContrastColor(color);
            }
        }

        tagList.appendChild(tagEl);
    }

    container.appendChild(tagList);
    return container;
}

/**
 * Calculate a contrasting text color (black or white) based on background.
 */
function getContrastColor(hexColor: string): string {
    if (!hexColor.startsWith('#')) {
        return '#ffffff';
    }

    let hex = hexColor.slice(1);
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
}
